import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import { useFetch } from '@/hooks/useFetch';

export interface CountryType {
  id: number;
  name: string;
  iso2?: string;
}

export interface CountrySelectorOption extends SelectorOption<string> {
  iso2?: string;
}

interface UseCountrySelectorProps {
  selectedId?: string | number | null;
  onSelect?: (option: SelectorOption<string> | null) => void;
  url?: string;
}

type CountriesApiResponse = {
  data: CountryType[];
  metadata?: {
    prioritizedCountriesList?: CountryType[];
  };
};

const mapCountryToOption = (country: CountryType): CountrySelectorOption => ({
  label: country.name,
  value: String(country.id),
  iso2: country.iso2,
});

export const useCountrySelector = ({ selectedId, onSelect, url = '/api/country' }: UseCountrySelectorProps) => {
  const { doFetch } = useFetch<void, CountriesApiResponse>();
  const [prioritizedCountries, setPrioritizedCountries] = useState<string[]>([]);

  const { data: countries = [], isFetching } = useQuery({
    queryKey: ['countries', url],
    queryFn: async (): Promise<CountrySelectorOption[]> => {
      const res = await doFetch(url);
      const { data, metadata } = res;
      const prioritized = metadata?.prioritizedCountriesList;
      setPrioritizedCountries(prioritized?.map((c) => c.iso2 ?? '') || []);

      if (prioritized?.length) {
        const prioritizedIds = new Set(prioritized.map((c) => c.id));
        const prioritizedOptions = prioritized.map(mapCountryToOption);
        const restOptions = data.filter((country) => !prioritizedIds.has(country.id)).map(mapCountryToOption);
        return [...prioritizedOptions, ...restOptions];
      }

      return data.map(mapCountryToOption);
    },
  });

  const countryOptions = useMemo(() => countries, [countries]);

  const selectedOption = useMemo<SelectorOption<string> | null>(() => {
    if (!selectedId) return null;
    return countryOptions.find((opt) => opt.value === String(selectedId)) ?? null;
  }, [countryOptions, selectedId]);

  const loadOptions = useCallback(
    async (inputValue: string): Promise<SelectorOption<string>[]> => {
      if (!inputValue.trim()) return countryOptions;
      return countryOptions.filter((opt) => opt.label.toLowerCase().includes(inputValue.toLowerCase()));
    },
    [countryOptions]
  );

  const handleSelect = useCallback(
    (option: SelectorOption<string> | null) => {
      onSelect?.(option ?? null);
    },
    [onSelect]
  );

  return {
    isFetching,
    countryOptions,
    selectedOption,
    loadOptions,
    handleSelect,
    prioritizedCountries,
  };
};